import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, Loader2, Users, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useConfirmDialog } from '@/components/useConfirmDialog';

const diasSemana = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado', 'Domingo'];
const initialForm = { nome: '', descricao: '', horario: '', local: '', dias_semana: [], max_alunos: '30', professor_ids: [] };

export default function TurmasPage() {
  const { user } = useAuth();
  const isGestor = ['gestor', 'socio'].includes(user?.role);
  const [turmas, setTurmas] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [professoresAbertos, setProfessoresAbertos] = useState({});
  const { confirm, ConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    const fetches = [api.get('/gestor/turmas')];
    if (isGestor) fetches.push(api.get('/gestor/colaboradores'));
    Promise.all(fetches)
      .then(([t, col]) => {
        setTurmas(t.data);
        if (col) {
          const profs = (col.data || []).filter(
              c =>
                c.status === 'ativo' &&
                (
                  c.role === 'professor' ||
                  c.acesso_staff === true
                )
            );
          setProfessores(profs);
        }
      })
      .catch(() => toast.error('Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [isGestor]);

  const refresh = async () => { const r = await api.get('/gestor/turmas'); setTurmas(r.data); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.professor_ids || form.professor_ids.length === 0) {
      toast.error('Selecione pelo menos um professor');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, max_alunos: parseInt(form.max_alunos) || 30 };
      if (editing) { await api.put(`/gestor/turmas/${editing.id}`, payload); toast.success('Turma atualizada'); }
      else { await api.post('/gestor/turmas', payload); toast.success('Turma criada'); }
      setDialogOpen(false); setEditing(null); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!await confirm('Remover Turma', 'Tem certeza que deseja remover esta turma?')) return;
    try { await api.delete(`/gestor/turmas/${id}`); toast.success('Turma removida'); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleDia = (dia) => {
    setForm(f => ({ ...f, dias_semana: f.dias_semana.includes(dia) ? f.dias_semana.filter(d => d !== dia) : [...f.dias_semana, dia] }));
  };

  const toggleProfessor = (profId) => {
    setForm(f => ({
      ...f,
      professor_ids: f.professor_ids.includes(profId)
        ? f.professor_ids.filter(id => id !== profId)
        : [...f.professor_ids, profId]
    }));
  };

  const openCreate = () => { setEditing(null); setForm(initialForm); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      nome: t.nome, descricao: t.descricao || '', horario: t.horario,
      local: t.local || '',
      dias_semana: t.dias_semana || [], max_alunos: String(t.max_alunos || 30),
      professor_ids: t.professor_ids || (t.professor_id ? [t.professor_id] : [])
    });
    setDialogOpen(true);
  };

  const getProfNames = (turma) => {
    if (turma.professor_nomes && turma.professor_nomes.length > 0) {
      return turma.professor_nomes.join(', ');
    }
    if (turma.professor_ids && turma.professor_ids.length > 0) {
      return turma.professor_ids.map(id => {
        const p = professores.find(pr => pr.id === id);
        return p ? p.name : id.slice(0, 6);
      }).join(', ');
    }
    return '-';
  };

  const getProfList = (turma) => {
    if (turma.professor_nomes && turma.professor_nomes.length > 0) {
      return turma.professor_nomes;
    }
    if (turma.professor_ids && turma.professor_ids.length > 0) {
      return turma.professor_ids.map(id => {
        const p = professores.find(pr => pr.id === id);
        return p ? p.name : id.slice(0, 6);
      });
    }
    return [];
  };

  const toggleProfessores = (turmaId) => {
    setProfessoresAbertos(prev => ({
      ...prev,
      [turmaId]: !prev[turmaId]
    }));
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="turmas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">TURMAS</h1>
        {isGestor && (
          <Button onClick={openCreate} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="add-turma-button">
            <Plus className="w-4 h-4 mr-2" /> Nova Turma
          </Button>
        )}
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-zinc-400">Nome</TableHead>
                <TableHead className="text-zinc-400">Professor(es)</TableHead>
                <TableHead className="text-zinc-400">Ocupação</TableHead>
                <TableHead className="text-zinc-400">Horario</TableHead>
                <TableHead className="text-zinc-400">Dias</TableHead>
                <TableHead className="text-zinc-400">Local</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                {isGestor && <TableHead className="text-zinc-400 text-right">Acoes</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-8">Carregando...</TableCell></TableRow>
              ) : turmas.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-8">Nenhuma turma cadastrada</TableCell></TableRow>
              ) : turmas.map(t => (
                <TableRow key={t.id} className="border-[#27272A] hover:bg-[#1C1C1E]">
                  <TableCell className="text-white font-medium">{t.nome}</TableCell>

                  <TableCell className="text-zinc-400 min-w-[170px]">
                    {(() => {
                      const lista = getProfList(t);
                      const aberto = !!professoresAbertos[t.id];

                      return (
                        <div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleProfessores(t.id)}
                            className="h-8 px-2 text-zinc-300 hover:text-white"
                          >
                            <Users className="w-4 h-4 mr-2" />
                            {lista.length === 0
                              ? 'Sem professores'
                              : `${lista.length} professor${lista.length > 1 ? 'es' : ''}`}
                            {lista.length > 0 && (
                              aberto
                                ? <ChevronUp className="w-4 h-4 ml-2" />
                                : <ChevronDown className="w-4 h-4 ml-2" />
                            )}
                          </Button>

                          {aberto && lista.length > 0 && (
                            <div className="mt-2 space-y-1 pl-2 border-l border-[#27272A]">
                              {lista.map((nome, index) => (
                                <div key={`${t.id}-prof-${index}`} className="text-xs text-zinc-400">
                                  {nome}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>

                  <TableCell className="min-w-[180px]">
                    {(() => {
                      const atual = Number(t.alunos_ativos || 0);
                      const maximo = Number(t.max_alunos || 0);
                      const vagas = Math.max(maximo - atual, 0);
                      const percentual = maximo > 0
                        ? Math.min((atual / maximo) * 100, 100)
                        : 0;

                      return (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between gap-3 text-xs">
                            <span className="text-white font-semibold">
                              {atual} / {maximo}
                            </span>
                            <span className={vagas === 0 ? 'text-amber-400' : 'text-zinc-500'}>
                              {vagas === 0 ? 'Lotada' : `${vagas} vaga${vagas > 1 ? 's' : ''}`}
                            </span>
                          </div>

                          <div className="h-2 w-full overflow-hidden rounded-full bg-[#27272A]">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${percentual}%`,
                                backgroundColor: 'var(--cor-primaria, #007AFF)'
                              }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>

                  <TableCell className="text-zinc-400">{t.horario}</TableCell>
                  <TableCell className="text-zinc-400 text-sm">{(t.dias_semana || []).join(', ')}</TableCell>

                  <TableCell className="text-zinc-400 min-w-[180px]">
                    {t.local ? (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-zinc-500" />
                        <span>{t.local}</span>
                      </div>
                    ) : (
                      <span className="text-zinc-600">Não informado</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-md font-medium ${t.ativo !== false ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'}`}>
                      {t.ativo !== false ? 'Ativa' : 'Inativa'}
                    </span>
                  </TableCell>
                  {isGestor && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="text-zinc-400 hover:text-white"><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isGestor && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">
                {editing ? 'Editar Turma' : 'Nova Turma'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome *</Label>
                <Input value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="turma-form-nome" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">
                  Professor(es) Responsavel(is) *
                  {form.professor_ids.length > 0 && (
                    <span className="ml-2 text-blue-400 font-normal">({form.professor_ids.length} selecionado{form.professor_ids.length > 1 ? 's' : ''})</span>
                  )}
                </Label>
                <div className="space-y-1.5 max-h-[140px] overflow-y-auto p-2 bg-[#0A0A0A] rounded-lg border border-[#27272A]" data-testid="turma-form-professors">
                  {professores.length === 0 ? (
                    <p className="text-[10px] text-amber-400">Nenhum professor ou STAFF disponivel.</p>
                  ) : professores.map(p => (
                    <label key={p.id} className="flex items-center gap-2 cursor-pointer py-1 px-1 rounded hover:bg-[#1C1C1E]">
                      <Checkbox
                        checked={form.professor_ids.includes(p.id)}
                        onCheckedChange={() => toggleProfessor(p.id)}
                        className="border-[#27272A] data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF]"
                      />
                      <span className="text-sm text-zinc-300">{p.name}</span>
                        {p.acesso_staff === true && (
                          <Badge
                            variant="outline"
                            className="ml-auto text-[9px] border-amber-500/30 text-amber-400"
                          >
                            STAFF
                          </Badge>
                        )}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Horario *</Label>
                <Input value={form.horario} onChange={e => setForm({...form, horario: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" placeholder="Ex: 08:00 - 09:30" required />
              </div>

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Local dos treinos</Label>
                <Input
                  value={form.local}
                  onChange={e => setForm({...form, local: e.target.value})}
                  className="bg-[#0A0A0A] border-[#27272A]"
                  placeholder="Ex: Colégio Militar Tiradentes III"
                  data-testid="turma-form-local"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Dias da Semana</Label>
                <div className="flex flex-wrap gap-2">
                  {diasSemana.map(dia => (
                    <label key={dia} className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox checked={form.dias_semana.includes(dia)} onCheckedChange={() => toggleDia(dia)} className="border-[#27272A] data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF]" />
                      <span className="text-sm text-zinc-300">{dia.substring(0, 3)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Max Alunos</Label>
                <Input type="number" value={form.max_alunos} onChange={e => setForm({...form, max_alunos: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descricao</Label>
                <Textarea value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400">Cancelar</Button>
                <Button type="submit" disabled={saving || form.professor_ids.length === 0} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="turma-form-submit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Salvar' : 'Criar')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
      <ConfirmDialog />
    </div>
  );
}
